import type { ODataQueryOptions } from './odata';

export enum Scope {
    Organization = 'organization',
    Tenant = 'tenant',
}

export type GovernancePolicyOperator = 'Or' | 'And' | 'None';

export interface GovernancePolicySelectorTags {
    values: string[];
    operator: GovernancePolicyOperator;
}

export interface GovernancePolicySelector {
    resourceType: GovernanceResourceType;
    values: string[];
    operator: GovernancePolicyOperator;
    tags?: GovernancePolicySelectorTags;
}

export interface GovernancePolicyActorValue {
    type: string;
    values: string[];
    operator: GovernancePolicyOperator;
}

export interface GovernancePolicyActorRule {
    values: GovernancePolicyActorValue[];
}

export interface GovernancePolicyExecutableValue {
    type: string;
    values: string[];
    operator: GovernancePolicyOperator;
}

export interface GovernancePolicyExecutableRule {
    values: GovernancePolicyExecutableValue[];
    tags?: {
        values: string[];
        operator: GovernancePolicyOperator;
    };
}

export interface GovernancePolicyFormData {
    name: string;
    policyType: string;
    description: string;
    organizationId: string;
    tenantId?: string;
    scope: Scope | string;
    enforcement: PolicyEnforcement;
    status: PolicyStatus;
    selectors?: Selector[];
    actorRule?: ActorRule;
    executableRule?: ExecutableRule;
    isBuiltIn?: boolean;
    isTemplate?: boolean;
}

export enum GovernanceResourceType {
    Agent = 'Agent',
    AgenticProcess = 'AgenticProcess',
    RPAWorkflow = 'RPAWorkflow',
    APIWorkflow = 'APIWorkflow',
    CaseManagement = 'CaseManagement',
    Flow = 'Flow',
}

export enum PolicyEnforcement {
    Allow = 'Allow',
    Deny = 'Deny',
    Prompt = 'Prompt',
    NoOp = 'NoOp',
}

export enum PolicyStatus {
    Active = 'Active',
    Disabled = 'Disabled',
    Simulated = 'Simulated',
    Deleted = 'Deleted',
}

export enum PolicyStatusFilter {
    All = 'All',
    Active = PolicyStatus.Active,
    Disabled = PolicyStatus.Disabled,
    Simulated = PolicyStatus.Simulated,
}

export enum ActorType {
    User = 'User',
    ExternalApplication = 'ExternalApplication',
    Robot = 'Robot',
    Group = 'Group',
}

export enum ExecutableType {
    Agent = 'Agent',
    AgenticProcess = 'AgenticProcess',
    CaseManagement = 'CaseManagement',
    Flow = 'Flow',
}

export interface TagRule {
    values: string[];
    operator: GovernancePolicyOperator;
}

export interface Selector {
    resourceType: GovernanceResourceType;
    values: string[];
    operator: GovernancePolicyOperator;
    tags?: TagRule;
}

export interface ActorValue {
    type: ActorType;
    values: string[];
    operator: GovernancePolicyOperator;
    /** Optional id → display-name map so the UI can show names after round-tripping. */
    valueNames?: Record<string, string>;
}

export interface ActorRule {
    values: ActorValue[];
}

export interface ExecutableValue {
    type: ExecutableType;
    values: string[];
    operator: GovernancePolicyOperator;
}

export interface ExecutableRule {
    values: ExecutableValue[];
    tags?: {
        values: string[];
        operator: GovernancePolicyOperator;
    };
}

export interface GovernancePolicy {
    policyType: string;
    organizationId: string;
    tenantId?: string;
    name: string;
    description: string;
    selectors?: Selector[];
    actorRule?: ActorRule;
    executableRule?: ExecutableRule;
    enforcement: PolicyEnforcement;
    status: PolicyStatus;
    isBuiltIn: boolean;
    isTemplate: boolean;
}

export interface GovernancePolicyEntity extends GovernancePolicy {
    id: string;
}

export interface GovernancePolicyEntityPagedResult {
    totalCount: number;
    results: GovernancePolicyEntity[];
}

export interface GetDataAccessPoliciesParams {
    accountId: string;
    top?: number;
    skip?: number;
    /**
     * Filter string in simplified OData format supporting only "in" and "and" operators.
     * Can be passed as a string or as ODataQueryOptions filter object.
     *
     * Supported filter fields:
     * - `createdBy`: List of GUIDs. Returns policies created by users with matching IDs.
     *   Example: `"createdBy in ('guid1','guid2')"` or `{ createdBy: { in: ['guid1', 'guid2'] } }`
     *
     * - `tenantId`: List of GUIDs. Returns policies within tenants with matching IDs.
     *   Example: `"tenantId in ('guid1')"` or `{ tenantId: { in: ['guid1'] } }`
     *
     * - `searchTerm`: List of strings. Returns policies where name or description contains the search term.
     *   Example: `"searchTerm in ('policy name','another term')"` or `{ searchTerm: { in: ['policy name'] } }`
     *
     * - `status`: List of PolicyStatus enum values (strings). Returns policies with matching status.
     *   Example: `"status in ('Active','Disabled')"` or `{ status: { in: ['Active', 'Disabled'] } }`
     *   where Active, Disabled, Simulated, Deleted
     *
     * Multiple filters can be combined with "and":
     * Example: `"tenantId in ('value1','value2') and createdBy in ('value3','value4')"`
     * Or: `{ filter: { tenantId: { in: ['value1', 'value2'] }, createdBy: { in: ['value3', 'value4'] } } }`
     */
    filter?: string | ODataQueryOptions['filter'];
    /**
     * Parameter for how to sort the paginated result.
     * Format: `{columnName} {asc|desc}`
     *
     * Supported order columns:
     * - `Name`
     * - `Description`
     * - `CreatedOn`
     * - `CreatedBy`
     * - `TenantId`
     *
     * Example: `orderBy=Name asc` or `orderBy=CreatedOn desc`
     */
    orderBy?: string;
}

// ─── Governance Traces ───────────────────────────────────────────────────────

export interface PolicyEvaluationTraceItem {
    startTime: string;
    policyEnforcement?: string;
    policyEvaluationResult?: string;
    policyName?: string;
    actorProcessId?: string;
    actorProcessType?: string;
    actorIdentityId?: string;
    actorIdentityType?: string;
    resourceId?: string;
    resourceType?: string;
    folderKey?: string;
    jobKey?: string;
    processKey?: string;
    tenantId?: string;
    // Detail pane fields
    traceId?: string;
    processName?: string;
    jobName?: string;
    folderName?: string;
    finalEnforcement?: string;
    justification?: string;
    policyStatus?: string;
    policyId?: string;
    policyEvaluationDetails?: string;
}

export interface PolicyEvaluationTracesResponse {
    items: PolicyEvaluationTraceItem[];
}

export interface PolicyEvaluationTracesRequest {
    startTime: string;
    endTime?: string;
    pageNumber?: number;
    pageSize?: number;
    evaluationResult?: string[];
    policyId?: string[];
    tenantId?: string[];
    actorProcessId?: string[];
    actorProcessType?: string[];
    actorIdentityId?: string[];
    actorIdentityType?: string[];
    resourceId?: string[];
    resourceType?: string[];
}

export interface GetPolicyEvaluationTracesParams {
    accountId: string;
    tenantId: string;
    body: PolicyEvaluationTracesRequest;
}

export interface GovernanceEvaluationSummaryResponse {
    totalEvaluations: number;
    allow: number;
    deny: number;
    noOp: number;
}

export interface PolicyEvaluationSummaryItem {
    policy?: string;
    totalEvaluations: number;
    allow: number;
    deny: number;
    noOpAllow: number;
    noOpDeny: number;
}

export interface PolicyEvaluationSummaryResponse {
    items: PolicyEvaluationSummaryItem[];
}

export interface GetPolicyEvaluationSummaryParams {
    accountId: string;
    tenantId: string;
    policy: string;
    startTime: string;
    endTime: string;
}

export interface GovernedOperationSummaryParams {
    accountId: string;
    tenantId: string;
    startTime: string;
    endTime: string;
}

export type TagEntity = {
    key: string;
    name: string;
    description: string | null;
    referencesCount: number;
    entityReferencesCount: Array<{
        entityType: string;
        count: number;
    }>;
    dataType: string;
    regex: string | null;
    type: string;
    accountKey: string;
    tenantKey: string;
    userKey: string;
    values: unknown[];
};
